import React, { useState, useEffect } from 'react';
import { DollarSign, Pencil } from 'lucide-react';
import { useProviderCompany } from '../../../../../context/ProviderCompanyContext';
import { updateProviderPricingPreferences } from '../../../../../services/staffingService';

const Pricing = ({ providerCompanyId }) => {
    const { providerCompanyData, fetchProviderCompanyData } = useProviderCompany();
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const defaults = {
        mode: 'fixed_margin',
        marginPct: 2,
        floorRate: 0,
        allocationMode: 'manual',
    };

    const [form, setForm] = useState(defaults);

    useEffect(() => {
        if (providerCompanyData?.preferences) {
            const p = providerCompanyData.preferences;
            setForm({
                mode: p.pricing?.mode || 'fixed_margin',
                marginPct: p.pricing?.marginPct != null ? Math.round(p.pricing.marginPct * 100) : 2,
                floorRate: p.pricing?.floorRate || 0,
                allocationMode: p.allocationMode || 'manual',
            });
        }
    }, [providerCompanyData]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await updateProviderPricingPreferences(providerCompanyId, {
                mode: form.mode,
                marginPct: parseFloat((form.marginPct / 100).toFixed(4)),
                floorRate: parseFloat(form.floorRate),
                allocationMode: form.allocationMode,
            });
            await fetchProviderCompanyData(providerCompanyId);
            setIsEditing(false);
        } catch (err) {
            setError('Failed to save pricing preferences.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const pricingLabel = form.mode === 'fixed_margin'
        ? `Fixed margin — ${form.marginPct}%`
        : 'Maximize margin (lowest valid officer rate)';

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <DollarSign size={18} className="text-gray-500" />
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Pricing & Allocation</h3>
                        <p className="text-xs text-gray-500">
                            Set agency margin, floor rate, and whether offers are sent automatically.
                        </p>
                    </div>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        <Pencil size={13} /> Edit
                    </button>
                )}
            </div>

            {!isEditing ? (
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Margin mode</p>
                        <p className="font-medium text-gray-800">{pricingLabel}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Floor rate ($/hr)</p>
                        <p className="font-medium text-gray-800">${form.floorRate}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Allocation mode</p>
                        <p className="font-medium text-gray-800">
                            {form.allocationMode === 'auto'
                                ? '⚡ Automatic — offers sent immediately'
                                : '👤 Manual — dispatcher confirms before sending'}
                        </p>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Margin mode */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">Margin mode</label>
                        <div className="flex gap-3">
                            {[
                                { value: 'fixed_margin', label: 'Fixed margin %' },
                                { value: 'maximize_margin', label: 'Maximize margin' },
                            ].map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleChange('mode', opt.value)}
                                    className={`px-4 py-2 rounded-lg text-xs font-medium border transition-colors ${
                                        form.mode === opt.value
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Margin % — only shown for fixed_margin */}
                    {form.mode === 'fixed_margin' && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Agency margin (%)
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max="50"
                                    step="0.5"
                                    value={form.marginPct}
                                    onChange={e => handleChange('marginPct', e.target.value)}
                                    className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-500">% of job hourlyPayRate</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                Officer will receive {100 - form.marginPct}% of the job rate.
                            </p>
                        </div>
                    )}

                    {/* Floor rate */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Floor rate ($/hr)
                        </label>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">$</span>
                            <input
                                type="number"
                                min="0"
                                step="0.50"
                                value={form.floorRate}
                                onChange={e => handleChange('floorRate', e.target.value)}
                                className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-500">minimum offer to any officer</span>
                        </div>
                    </div>

                    {/* Allocation mode */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                            Allocation mode
                        </label>
                        <div className="flex gap-3">
                            {[
                                { value: 'manual', label: '👤 Manual', desc: 'Dispatcher reviews and confirms' },
                                { value: 'auto', label: '⚡ Automatic', desc: 'Offers sent immediately on job create' },
                            ].map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleChange('allocationMode', opt.value)}
                                    className={`flex-1 px-4 py-3 rounded-lg text-xs border text-left transition-colors ${
                                        form.allocationMode === opt.value
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-400'
                                    }`}
                                >
                                    <p className={`font-semibold ${form.allocationMode === opt.value ? 'text-blue-700' : 'text-gray-700'}`}>
                                        {opt.label}
                                    </p>
                                    <p className="text-gray-500 mt-0.5">{opt.desc}</p>
                                </button>
                            ))}
                        </div>
                        {form.allocationMode === 'auto' && (
                            <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded-lg px-3 py-2">
                                ⚠ In automatic mode, rank-1 officers are offered immediately when a job is created.
                                Enable only once you've tested match quality on your agency.
                            </p>
                        )}
                    </div>

                    {error && <p className="text-xs text-red-500">{error}</p>}

                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-opacity disabled:opacity-60"
                            style={{ backgroundColor: 'var(--primary)' }}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default Pricing;
